import { useEffect, useState} from "react";
import FontBody1 from "../../../components/fonts/font-body1/font-body1";
import {  FONT_COLOR, FONT_VARIANT } from "../../../components/fonts/font-props";
import IconFunnel from "../../../components/icons/icon-funnel/icon-funnel";
import { BUTTON_VARIANTS } from "../../../components/inputs/button-variants";
import { Button } from "../../../components/inputs/button/button";
import Card from "../../../components/layouts/card/card";
import SearchBar from "../../../components/layouts/search-bar/search-bar";
import SearchInput from "../../../components/search-input/search-input";
import TableRow from "../../../components/table/components/table-row/table-row";
import Table from "../../../components/table/table";
import dashboardTranslationsStore from "../../../dashboard-translation/stores/dashboard-translations-store";
import { IFiltersButtonstyle } from "../stores/shipments-list-filters-store";
import { observer } from "mobx-react";
import DateTimeV2, { DATETIME_VIEW } from "../../../components/datetime-v2/datetime-v2";
import { useDateTimeFormattingParams } from "../../../hooks/use-datetime-formatting-params";
import ActiveFiltersTags from "./components/active-filters-tags/active-filters-tags";
import CopyButton from "./components/copy-button/copy-button";
import ShipmentsListFiltersDrawer from "./components/shipments-list-filters-drawer/shipments-list-filters-drawer";
import { TABLE_SPACING_VARIANT } from "../../../components/table/table.types";
import { TOOLTIP_VARIANTS } from "../../../components/tooltip/tooltip";
import { Tooltip } from "../../../components/tooltip/tooltip";
import { TOOLTIP_PLACEMENTS } from "../../../components/tooltip/tooltip-box";
import ShipmentItemStore from "../stores/shipment-item-store";
import shipmentsStore from "../stores/shipments-store";
import Currency from "../../../components/currency/currency";
import ColumnTitleWithTooltip from "./components/column-title-with-tooltip/column-title-with-tooltip";
import "./shipments-list-page.scss";
import { SHIPMENT_STATUS_TRANSLATION_KEYS_MAP } from "../shipment.types";
import ShipmentsStatusChip from "./components/shipments-status-chip/shipments-status-chip";
import AHref from "../../../components/ahref/ahref";
import config from "../../../config";
import ExternalLink from "../../manage-returns/components/external-link/external-link";
import FontBody2 from "../../../components/fonts/font-body2/font-body2";
import RichReportButton from "../../../components/rich-report-button/rich-report-button";
import { REPORT_TYPES } from "../../../services/api/report-service";
import currentPortalStore from "../../../stores/current-portal-store";
import { FEATURES } from "../../../models/Plan";
import useDashboardTranslation from "../../../dashboard-translation/hooks/dashboard-translation-hook";
const shipmentsListFiltersStore = shipmentsStore.shipmentsListFiltersStore;

const ShipmentsListPage: React.FC = observer(() => {

    const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
    const { getTranslation } = useDashboardTranslation();
    const dateTimeParams = useDateTimeFormattingParams();

    const isLoading = shipmentsStore.isLoading ||
    dashboardTranslationsStore.isLoading ||
    shipmentsListFiltersStore.isLoading;

    useEffect(() => {
        shipmentsStore.load();
        shipmentsListFiltersStore.loadFilterOptions();
    }, []);

    const onPaginationSetPage = async (page: number) => {
        shipmentsStore.resetIsLoaded();
        window.scrollTo(0, 0);
        await shipmentsStore.loadPage(page);
    }


    const filtersButtonStyle= ():IFiltersButtonstyle => {
        if (shipmentsListFiltersStore.hasActiveDrawerFilters()) {
            return { variant: BUTTON_VARIANTS.PrimaryAccent, color: 'var(--color-neutral-1-white)'};
        }
        return {variant: BUTTON_VARIANTS.Secondary, color: 'var(--color-accent-1)'};
    }

    const isFeatureAllowed = currentPortalStore.isFeatureAllowed(FEATURES.BETA_TRACKING_DB_REPORT) && currentPortalStore.isFeatureAllowed(FEATURES.BETA_TRACKING_REPORT);

    return (
        <>
            <Card>
                <SearchBar>
                    <SearchInput
                        placeholder={'Tracking #, order #, customer id'}
                        value={shipmentsListFiltersStore.searchInput ?? ''}
                        onClick={() => shipmentsStore.loadPage(1)}
                        onChange={(value: string) => {
                            shipmentsListFiltersStore.setSearchValue(value);
                        }}
                    />
                    {<Button
                        variant={filtersButtonStyle().variant}
                        leftIcon={() => <IconFunnel color={filtersButtonStyle().color} />}
                        text={'Filters'}
                        onClick={() => setShowFiltersDrawer(true)}
                    />}
                    <div className="shipments-page-report-buttons">
                        {isFeatureAllowed &&
                            <RichReportButton title="ORDER TRACKING REPORT" type={REPORT_TYPES.TRACKING} getReportFilters={() => shipmentsListFiltersStore.getReportFilters()} />
                        }
                        <RichReportButton title="REPORT" type={REPORT_TYPES.SHIPMENT_NEW} getReportFilters={() => shipmentsListFiltersStore.getReportFilters()} />
                    </div>
                </SearchBar>
                {showFiltersDrawer && <ShipmentsListFiltersDrawer 
                    onClose={() => setShowFiltersDrawer(false)}
                    shipmentsListFiltersStore={shipmentsListFiltersStore}
                />}
                <ActiveFiltersTags shipmentsListFiltersStore={shipmentsListFiltersStore} />
                <div className="shipments-table">
                    <Table
                        isLoading={isLoading}
                        totalResultsTitle='shipments'
                        pagination={{
                            onSetPage: onPaginationSetPage,
                            page: shipmentsStore.page,
                            total: shipmentsStore.total,
                            perPage: shipmentsStore.perPage,
                        }}
                        sort={{
                            onSort: (by: string, dir: string) => {
                                shipmentsStore.sortData(by, dir);
                            },
                            by: shipmentsStore.sortBy,
                            dir: shipmentsStore.sortDir,
                        }}
                        columns={[
                            { content: 'RMA ID', width: { value: 90, type: 'px' }, sortable: true, sortBy: 'return_id' },
                            { content: 'ORDER', width: { value: 90, type: 'px' }, sortable: true, sortBy: 'order_name' },
                            { content: 'TRACKING', width: { value: 1.25, type: 'fr' }, sortable: true, sortBy: 'tracking_number' },
                            { content: 'STATUS', width: { value: 0.9, type: 'fr' }, sortable: true, sortBy: 'status' },
                            { content: 'CARRIER', width: { value: 0.7, type: 'fr' }, sortable: true, sortBy: 'carrier' },
                            { content: 'RETURN METHOD TYPE', width: { value: 1.2, type: 'fr' }, sortable: true, sortBy: 'return_method' },
                            { 
                                content: <ColumnTitleWithTooltip 
                                title="SHIPMENT CREATED DATE"
                                tooltipContent="Date of shipment creation in ReturnGO"
                            />, 
                                width: { value: 1, type: 'fr' },
                                sortable: true,
                                sortBy: 'created_at'
                            },
                            { content: 'CUSTOMER', width: { value: 90, type: 'px' }, sortable: true, sortBy: 'customer_id' },
                            { content: <ColumnTitleWithTooltip 
                                title="LABEL COST"
                                tooltipContent="Cost to generate the label. *Not all shipping integrations provide label costs"
                            />,
                                width: { value: 0.6, type: 'fr' },
                                sortable: true,
                                sortBy: 'label_cost' 
                            },
                        ]}
                        spacingVariant={TABLE_SPACING_VARIANT.DEFAULT}
                    >
                        {/* TODO: remove div.text-truncate-simple when TruncateText component is fixed */}
                        {shipmentsStore.list.map((shipment: ShipmentItemStore) => {
                            return (
                                <TableRow key={shipment.id} skipWrapingCell={true} cells={[
                                    <div className="shipment-rma-id-container">
                                        <Tooltip content={
                                            <FontBody1 color={FONT_COLOR.NEUTRAL_WHITE}>
                                                {shipment.returnId ? 'RMA' + shipment.returnId : 'N/A'}
                                            </FontBody1>
                                        } placement={TOOLTIP_PLACEMENTS.BOTTOM} variant={TOOLTIP_VARIANTS.INFO}>
                                            <div className="text-truncate-simple">
                                                <AHref href={`${config.apiUrl}/dashboard/returns?filter_status=open&rmaid=${shipment.returnId}`} target="_parent">
                                                    <FontBody2 color={FONT_COLOR.ACCENT1} variant={FONT_VARIANT.UNDER_LINE}>
                                                        {shipment.returnId ? 'RMA' + shipment.returnId : 'N/A'}
                                                    </FontBody2>
                                                </AHref>
                                            </div>
                                        </Tooltip>
                                    </div>,
                                    <div className="shipment-order-number-container">
                                        <Tooltip content={
                                            <FontBody1 color={FONT_COLOR.NEUTRAL_WHITE}>
                                                {shipment.orderNumber ?? 'N/A'}
                                            </FontBody1>
                                        } placement={TOOLTIP_PLACEMENTS.BOTTOM} variant={TOOLTIP_VARIANTS.INFO}>
                                            <div className="text-truncate-simple">
                                                {shipment.orderUrl ? 
                                                    <ExternalLink label={shipment.orderNumber ?? 'N/A'} url={shipment.orderUrl} Font={FontBody2} />
                                                    :  <FontBody2>{shipment.orderNumber ?? 'N/A'}</FontBody2>}
                                            </div>
                                        </Tooltip>
                                    </div>,
                                    <div className="shipment-tracking-number-container">
                                        <Tooltip content={
                                            <FontBody1 color={FONT_COLOR.NEUTRAL_WHITE}>
                                                {shipment.trackingNumber ?? 'N/A'}
                                            </FontBody1>
                                        } placement={TOOLTIP_PLACEMENTS.BOTTOM} variant={TOOLTIP_VARIANTS.INFO}>
                                            {shipment.trackingUrl ? 
                                                <ExternalLink label={shipment.trackingNumber ?? 'N/A'} url={shipment.trackingUrl} Font={FontBody2} />
                                                :  <FontBody2>{shipment.trackingNumber ?? 'N/A'}</FontBody2>}
                                        </Tooltip>
                                        {shipment.trackingNumber ? <CopyButton value={shipment.trackingNumber} /> : ''}
                                    </div>,
                                    <ShipmentsStatusChip statusCode={shipment.status ?? 0} status={SHIPMENT_STATUS_TRANSLATION_KEYS_MAP[shipment.status as keyof typeof SHIPMENT_STATUS_TRANSLATION_KEYS_MAP] ?? String(shipment.status)} />,
                                    <div className="shipment-carrier-container">
                                        <FontBody2  color={FONT_COLOR.PRIMARY3}>{shipment.carrier ?? 'N/A'}</FontBody2>
                                    </div>,
                                    <div className="shipment-return-method-type-container">
                                        <FontBody2  color={FONT_COLOR.PRIMARY3}>{shipment.returnMethodType ? getTranslation(shipment.returnMethodType, 'dashboard-return-method-editor') : 'N/A'}</FontBody2>
                                    </div>,
                                    <FontBody2>
                                        {shipment.createDate ? <DateTimeV2 {...dateTimeParams} utcDateString={shipment.createDate} dateTimeView={DATETIME_VIEW.DATE_ONLY}/> : 'N/A'}
                                    </FontBody2>,
                                    <div className="shipment-customer-container">
                                        <Tooltip content={
                                            <FontBody1 color={FONT_COLOR.NEUTRAL_WHITE}>
                                                {shipment.customerId ?? 'N/A'}
                                            </FontBody1>
                                        } placement={TOOLTIP_PLACEMENTS.BOTTOM} variant={TOOLTIP_VARIANTS.INFO}>
                                            <div className="text-truncate-simple">
                                                {shipment.customerUrl ? 
                                                    <ExternalLink label={shipment.customerId ?? 'N/A'} url={shipment.customerUrl} Font={FontBody2} />
                                                    :  <FontBody2>{shipment.customerId ?? 'N/A'}</FontBody2>}
                                            </div>
                                        </Tooltip>
                                    </div>,
                                    <div className="shipment-label-cost-container">
                                        <FontBody2  color={FONT_COLOR.PRIMARY3}>
                                            {shipment.labelCost && shipment.paymentCurrencyCode ? <Currency currencyCode={shipment.paymentCurrencyCode} amount={shipment.labelCost}/> : 'N/A'}
                                        </FontBody2>
                                    </div>,
                                ]}/>
                            );
                        })}
                    </Table>
                </div>
            </Card>
        </>
    );
});

export default ShipmentsListPage;
